import { useEffect, useMemo, useState } from "react";
import { Eye, EyeOff, KeyRound, ShieldCheck, AlertCircle, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/formatApiError";

const POLICY_BLOCKLIST = [
    "admin",
    "password",
    "letmein",
    "qwerty",
    "12345678",
    "welcome",
    "changeme",
    "changeme-2025!",
    "operator",
    "master",
    "uavsimnet",
];

function evaluatePolicy(pw) {
    return {
        long_enough: pw.length >= 10,
        within_72b: new Blob([pw]).size <= 72,
        has_letter: /[A-Za-z]/.test(pw),
        has_digit: /\d/.test(pw),
        not_common: pw && !POLICY_BLOCKLIST.includes(pw.toLowerCase()),
    };
}

function PolicyRow({ ok, label, testid }) {
    const Icon = ok ? CheckCircle2 : AlertCircle;
    return (
        <li
            data-testid={testid}
            className={`flex items-center gap-2 text-xs ${
                ok ? "text-ux-success" : "text-ux-text-3"
            }`}
        >
            <Icon className="h-3.5 w-3.5 shrink-0" />
            <span>{label}</span>
        </li>
    );
}

export default function ChangePasswordPage() {
    const navigate = useNavigate();
    const { user, changePassword, logout, mustChangePassword } = useAuth();

    const [currentPw, setCurrentPw] = useState("");
    const [newPw, setNewPw] = useState("");
    const [confirmPw, setConfirmPw] = useState("");
    const [showNew, setShowNew] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);

    const policy = useMemo(() => evaluatePolicy(newPw), [newPw]);
    const policyAllPass = useMemo(
        () => Object.values(policy).every(Boolean),
        [policy],
    );
    const confirmMatches = newPw && newPw === confirmPw;
    const canSubmit =
        Boolean(currentPw)
        && policyAllPass
        && confirmMatches
        && newPw !== currentPw
        && !submitting;

    useEffect(() => {
        if (!user) navigate("/login", { replace: true });
    }, [user, navigate]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        setErrorMsg(null);
        try {
            await changePassword(currentPw, newPw);
            toast.success("Password changed — please sign in again.");
            // Server has bumped pw_ver — current token is no longer valid.
            await logout();
            navigate("/login", { replace: true });
        } catch (err) {
            const detail =
                formatApiError(err, "Could not change password.");
            setErrorMsg(detail);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="min-h-[calc(100vh-3.5rem)] w-full flex items-center justify-center px-4 py-10"
            data-testid="change-password-page"
        >
            <div className="w-full max-w-[480px] rounded-md border border-ux-border-1 bg-ux-bg-1 shadow-[0_30px_80px_rgba(0,0,0,0.45)]">
                <header className="px-6 pt-6 pb-3 border-b border-ux-border-1">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-ux-text-3">
                        <KeyRound className="h-3.5 w-3.5" />
                        Account · password
                    </div>
                    <h1 className="mt-1 text-xl font-semibold text-ux-text-1">
                        {mustChangePassword ? "Set a new password" : "Change password"}
                    </h1>
                    <p className="mt-1 text-sm text-ux-text-3">
                        {mustChangePassword
                            ? "Your account was created with a temporary password. Choose a new one to continue."
                            : "Choose a strong password. All existing sessions will be ended."}
                    </p>
                </header>

                <form onSubmit={onSubmit} className="px-6 pb-6 pt-4 space-y-4">
                    <div className="space-y-1.5">
                        <Label htmlFor="cp-current" className="text-xs uppercase tracking-wider text-ux-text-3">
                            Current password
                        </Label>
                        <Input
                            id="cp-current"
                            data-testid="change-password-current-input"
                            type="password"
                            autoComplete="current-password"
                            value={currentPw}
                            onChange={(e) => setCurrentPw(e.target.value)}
                            className="bg-ux-bg-2 border-ux-border-1 text-ux-text-1 focus-visible:ring-ux-accent"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="cp-new" className="text-xs uppercase tracking-wider text-ux-text-3">
                            New password
                        </Label>
                        <div className="relative">
                            <Input
                                id="cp-new"
                                data-testid="change-password-new-input"
                                type={showNew ? "text" : "password"}
                                autoComplete="new-password"
                                value={newPw}
                                onChange={(e) => setNewPw(e.target.value)}
                                className="bg-ux-bg-2 border-ux-border-1 text-ux-text-1 focus-visible:ring-ux-accent pr-10"
                            />
                            <button
                                type="button"
                                onClick={() => setShowNew((v) => !v)}
                                aria-label={showNew ? "Hide new password" : "Show new password"}
                                data-testid="change-password-toggle"
                                className="absolute inset-y-0 right-2 flex items-center text-ux-text-3 hover:text-ux-text-1 transition-colors"
                            >
                                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                    </div>

                    <ul
                        className="grid grid-cols-1 gap-1.5 rounded-sm bg-ux-bg-2 border border-ux-border-1 px-3 py-2.5"
                        data-testid="change-password-policy"
                    >
                        <PolicyRow ok={policy.long_enough} label="At least 10 characters" testid="cp-policy-long" />
                        <PolicyRow ok={policy.has_letter} label="Contains a letter" testid="cp-policy-letter" />
                        <PolicyRow ok={policy.has_digit} label="Contains a digit" testid="cp-policy-digit" />
                        <PolicyRow ok={policy.not_common} label="Not a common password" testid="cp-policy-common" />
                        <PolicyRow ok={policy.within_72b} label="Within 72 bytes" testid="cp-policy-bytes" />
                    </ul>

                    <div className="space-y-1.5">
                        <Label htmlFor="cp-confirm" className="text-xs uppercase tracking-wider text-ux-text-3">
                            Confirm new password
                        </Label>
                        <Input
                            id="cp-confirm"
                            data-testid="change-password-confirm-input"
                            type="password"
                            autoComplete="new-password"
                            value={confirmPw}
                            onChange={(e) => setConfirmPw(e.target.value)}
                            className="bg-ux-bg-2 border-ux-border-1 text-ux-text-1 focus-visible:ring-ux-accent"
                        />
                        {confirmPw && !confirmMatches && (
                            <div className="text-xs text-ux-danger" data-testid="change-password-confirm-error">
                                Passwords do not match.
                            </div>
                        )}
                    </div>

                    {errorMsg && (
                        <div
                            className="flex items-start gap-2 rounded-sm border border-ux-danger/40 bg-ux-danger-soft px-3 py-2 text-sm text-ux-danger"
                            data-testid="change-password-error"
                            role="alert"
                        >
                            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                            <span>{errorMsg}</span>
                        </div>
                    )}

                    <div className="flex items-center justify-between gap-3 pt-1">
                        <span className="flex items-center gap-1.5 text-[11px] text-ux-text-3">
                            <ShieldCheck className="h-3.5 w-3.5" />
                            All existing sessions will be invalidated.
                        </span>
                        <Button
                            type="submit"
                            data-testid="change-password-submit-button"
                            disabled={!canSubmit}
                            className="bg-ux-accent text-ux-bg-0 hover:bg-ux-accent/90 disabled:opacity-50"
                        >
                            {submitting ? "Saving…" : "Update password"}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
